/**
 * MeContextProvider — bootstraps `me-context` once per signed-in session
 * per `docs/frontend/035-frontend-app-shell-plan.md §3.1`.
 *
 * State machine:
 *   - `idle`: provider has not resolved supabase auth state yet (very brief).
 *   - `unauthenticated`: no supabase session — public routes only.
 *   - `loading`: supabase session present, fetching me-context.
 *   - `ready`: me-context resolved successfully.
 *   - `no-tenant`: 404 from me-context (no active profile + not sysadmin).
 *   - `error`: any other failure.
 *
 * The provider re-fetches me-context whenever the supabase access token
 * changes (sign-in / refresh / sign-out); manual refresh is exposed for
 * future mutation flows that need it (Task 037+).
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type { Session } from '@supabase/supabase-js';
import { supabase } from '../auth/supabase';
import { clearRehydrationBudgets } from '../auth/activationRehydrationBudget';
import { isApiError } from '../api/client';
import { getMeContext } from '../api/endpoints';
import type { MeContext } from '../api/types';

export type MeStatus = 'idle' | 'unauthenticated' | 'loading' | 'ready' | 'no-tenant' | 'error';

export interface MeContextValue {
  readonly status: MeStatus;
  readonly me: MeContext | null;
  readonly session: Session | null;
  readonly error: unknown;
  readonly refresh: () => void;
  readonly signOut: () => Promise<void>;
}

const Ctx = createContext<MeContextValue | null>(null);
const ME_CACHE_PREFIX = 'kmi:me-context:';

/**
 * Task 306A §4 — is this snapshot the NON-OPERATIONAL shape?
 *
 * `app.current_org_id()` resolves NULL for a tenant that is not
 * `activated`, so `me/context` answers a real agency owner of a pending,
 * suspended or terminated tenant with
 * `organization: null, roles: [], permissions: []`. A system admin is
 * collapsed to a superficially similar shape, but carries
 * `system_admin: true` and is excluded here — the platform surfaces
 * genuinely render from it.
 *
 * A tenant actor's snapshot with no organization can therefore only be
 * pre-activation residue. It can render nothing, and replaying it as
 * `ready` is what put an activated owner in front of an empty product for
 * the length of one network round trip.
 *
 * Exported so the predicate the provider and the standing gate both depend
 * on has exactly one definition.
 */
export function isNonOperationalContext(context: MeContext | null | undefined): boolean {
  return Boolean(context) && context!.system_admin === false && context!.organization === null;
}

function readCachedMe(userId: string): MeContext | null {
  try {
    const raw = window.localStorage.getItem(`${ME_CACHE_PREFIX}${userId}`);
    if (!raw) return null;
    return JSON.parse(raw) as MeContext;
  } catch {
    return null;
  }
}

function writeCachedMe(userId: string, nextMe: MeContext): void {
  try {
    window.localStorage.setItem(`${ME_CACHE_PREFIX}${userId}`, JSON.stringify(nextMe));
  } catch {
    // Cache is a UX optimisation only. Private-mode / quota failures are non-fatal.
  }
}

function clearCachedMe(userId: string | null): void {
  if (!userId) return;
  try {
    window.localStorage.removeItem(`${ME_CACHE_PREFIX}${userId}`);
  } catch {
    // Ignore cache cleanup failures.
  }
}

export function MeContextProvider({ children }: { readonly children: ReactNode }) {
  const [session, setSession] = useState<Session | null | undefined>(undefined);
  const [me, setMe] = useState<MeContext | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [status, setStatus] = useState<MeStatus>('idle');
  const [refreshKey, setRefreshKey] = useState(0);
  const requestRef = useRef<AbortController | null>(null);

  // Subscribe to supabase auth state. Initial getSession + onAuthStateChange.
  useEffect(() => {
    let mounted = true;
    void supabase.auth.getSession().then(({ data }) => {
      if (mounted) setSession(data.session);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      if (mounted) setSession(nextSession);
    });
    return () => {
      mounted = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  // Resolve me-context whenever the session changes or refresh is requested.
  useEffect(() => {
    if (session === undefined) {
      setStatus('idle');
      return;
    }
    if (session === null) {
      setMe(null);
      setError(null);
      setStatus('unauthenticated');
      // Task 306A §4.2.1 — the session is gone, so the activation
      // rehydration budgets have nobody to belong to. They are cleared HERE
      // and not in `CommercialStandingGate`, which lives inside `AuthGate`
      // and is already unmounted by the time this status exists: an effect
      // there would look correct and never run. This branch covers token
      // expiry and an externally revoked session.
      clearRehydrationBudgets();
      return;
    }
    const cached = readCachedMe(session.user.id);
    // Task 306A §4.2 — a NON-OPERATIONAL snapshot is never replayed as
    // `ready`. It carries no organization and no permissions, so admitting
    // on it renders an empty product; after the operator activates the
    // tenant it is also stale, and the staleness is invisible because the
    // shape is valid. Treat it as a cold start and let `AuthGate` show its
    // session skeleton until the real answer lands. The fetch below is
    // unchanged and still runs in both branches.
    if (cached && !isNonOperationalContext(cached)) {
      setMe(cached);
      setStatus('ready');
    } else {
      setStatus('loading');
    }
    setError(null);
    requestRef.current?.abort();
    const ctrl = new AbortController();
    requestRef.current = ctrl;
    getMeContext({ signal: ctrl.signal })
      .then((data) => {
        if (ctrl.signal.aborted) return;
        setMe(data);
        writeCachedMe(session.user.id, data);
        setStatus('ready');
      })
      .catch((err: unknown) => {
        if (ctrl.signal.aborted) return;
        if (err instanceof DOMException && err.name === 'AbortError') return;
        if (isApiError(err)) {
          if (err.code === 'unauthenticated') {
            setMe(null);
            clearCachedMe(session.user.id);
            // Task 306A §4.2.1 — the THIRD route to `unauthenticated`, and
            // the one a binding review found missing: a 401 from
            // `me/context` sets the status directly, without waiting for a
            // `session === null` event. A rehydration attempt spent just
            // before it would otherwise survive the user signing back in.
            clearRehydrationBudgets();
            setStatus('unauthenticated');
            return;
          }
          if (err.code === 'not_found') {
            setMe(null);
            setStatus('no-tenant');
            return;
          }
        }
        setError(err);
        setStatus('error');
      });
    return () => ctrl.abort();
  }, [session, refreshKey]);

  const refresh = useCallback(() => {
    // Task 299 — a manual standing refresh may follow operator activation.
    // Discard the denied pre-activation snapshot before fetching so the gate
    // never opens the product over a stale empty-permissions cache.
    const userId = session?.user.id ?? null;
    clearCachedMe(userId);
    if (session) setStatus('loading');
    setRefreshKey((n) => n + 1);
  }, [session]);

  const signOut = useCallback(async () => {
    const userId = session?.user.id ?? null;
    await supabase.auth.signOut();
    clearCachedMe(userId);
    // Cleared here TOO, not only in the `session === null` branch above:
    // this path sets `unauthenticated` directly, without waiting for the
    // auth listener, so relying on that branch alone would leave a budget
    // spent for however long the listener takes to fire.
    clearRehydrationBudgets();
    setMe(null);
    setError(null);
    setStatus('unauthenticated');
  }, [session]);

  const value = useMemo<MeContextValue>(
    () => ({
      status,
      me,
      session: session ?? null,
      error,
      refresh,
      signOut,
    }),
    [status, me, session, error, refresh, signOut],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useMeContext(): MeContextValue {
  const v = useContext(Ctx);
  if (!v) throw new Error('useMeContext must be used inside MeContextProvider');
  return v;
}
